// @flow
import * as React from 'react'
import styled from 'styled-components'
import { colors } from '@components/shared'
import { Col, Visible, Row, config } from 'react-awesome-styled-grid'
import type { ContentBlockCenteredTextAndImageType } from '../../../services/contentfulMarketingLandingPages'
import SharedCta from '../SharedCta'
import { FadeInImage } from '@components/shared/FadeInImage'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import { MARKS } from '@contentful/rich-text-types'
import { landingPageBackgroundColor } from '../../../services/contentfulMarketingLandingPages'
import { BlockSection } from './sectionStyles'
import { OutlineOnFocusVisible } from '@components/shared/buttons'

const options = {
  renderNode: {
    [MARKS.CODE]: (node: any, children: any): React.Node => {
      return <div dangerouslySetInnerHTML={{ __html: children }} />
    }
  }
}

export default function CenteredTextAndImage (props: {
  block: ContentBlockCenteredTextAndImageType,
  isFirst: boolean,
  isSingleContentBlock: boolean
}): React.Node {
  const {
    id,
    backgroundImage,
    backgroundColor,
    headerText,
    descriptionText,
    cta,
    mobileBottomImage,
    tabletBottomImage,
    desktopBottomImage,
    imageLink
  } = props.block

  const description = documentToReactComponents(descriptionText, options)
  const isWhiteText = backgroundColor === landingPageBackgroundColor.glow

  return (
    <BlockSection
      sectionId={id}
      backgroundColor={backgroundColor}
      backgroundImage={backgroundImage}
      isSingleContentBlock={props.isSingleContentBlock}
      isFirst={props.isFirst}
    >
      <Row align='center'>
        <Col
          xs={4}
          sm={8}
          md={6}
          lg={8}
          xl={8}
          align='center'
          offset={{ md: 1, lg: 2 }}
        >
          <TextContainer>
            {!!headerText && (
              <Header isWhiteText={isWhiteText}>{headerText}</Header>
            )}
            {!!descriptionText && (
              <Description isWhiteText={isWhiteText}>{description}</Description>
            )}
            {!!cta && (
              <CtaContainer>
                <SharedCta cta={cta} />
              </CtaContainer>
            )}
          </TextContainer>
        </Col>
      </Row>

      <Row align='center'>
        <Col xs={4} sm={8} md={8} lg={10} align='center' offset={{ lg: 1 }}>
          {!!mobileBottomImage.url &&
            !!mobileBottomImage.height &&
            !!mobileBottomImage.width && (
            <Visible xs={true} sm={true} md={false} lg={false} xl={false}>
              {imageLink
                ? (
                <BottomImageContainerLink
                  target='_blank'
                  rel='noopener noreferrer'
                  href={`${imageLink}`}
                >
                  <FadeInImage
                    src={`https:${mobileBottomImage.url}`}
                    height={mobileBottomImage.height}
                    width={mobileBottomImage.width}
                    fadeInTime={0.3}
                    styles={{ justifyContent: 'center' }}
                  />
                </BottomImageContainerLink>
                  )
                : (
                <BottomImageContainer>
                  <FadeInImage
                    src={`https:${mobileBottomImage.url}`}
                    height={mobileBottomImage.height}
                    width={mobileBottomImage.width}
                    fadeInTime={0.3}
                    styles={{ justifyContent: 'center' }}
                  />
                </BottomImageContainer>
                  )}
            </Visible>
          )}
          {!!tabletBottomImage.url &&
            !!tabletBottomImage.height &&
            !!tabletBottomImage.width && (
            <Visible xs={false} sm={false} md={true} lg={false} xl={false}>
              {imageLink
                ? (
                <BottomImageContainerLink
                  target='_blank'
                  rel='noopener noreferrer'
                  href={`${imageLink}`}
                >
                  <FadeInImage
                    src={`https:${tabletBottomImage.url}`}
                    height={tabletBottomImage.height}
                    width={tabletBottomImage.width}
                    fadeInTime={0.3}
                    styles={{ justifyContent: 'center' }}
                  />
                </BottomImageContainerLink>
                  )
                : (
                <BottomImageContainer>
                  <FadeInImage
                    src={`https:${tabletBottomImage.url}`}
                    height={tabletBottomImage.height}
                    width={tabletBottomImage.width}
                    fadeInTime={0.3}
                    styles={{ justifyContent: 'center' }}
                  />
                </BottomImageContainer>
                  )}
            </Visible>
          )}
          {!!desktopBottomImage.url &&
            !!desktopBottomImage.height &&
            !!desktopBottomImage.width && (
            <Visible xs={false} sm={false} md={false} lg={true} xl={true}>
              {imageLink
                ? (
                <BottomImageContainerLink
                  target='_blank'
                  rel='noopener noreferrer'
                  href={`${imageLink}`}
                >
                  <FadeInImage
                    src={`https:${desktopBottomImage.url}`}
                    height={desktopBottomImage.height}
                    width={desktopBottomImage.width}
                    fadeInTime={0.3}
                    styles={{ justifyContent: 'center' }}
                  />
                </BottomImageContainerLink>
                  )
                : (
                <BottomImageContainer>
                  <FadeInImage
                    src={`https:${desktopBottomImage.url}`}
                    height={desktopBottomImage.height}
                    width={desktopBottomImage.width}
                    fadeInTime={0.3}
                    styles={{ justifyContent: 'center' }}
                  />
                </BottomImageContainer>
                  )}
            </Visible>
          )}
        </Col>
      </Row>
    </BlockSection>
  )
}

const BottomImageContainer = styled.div`
  position: relative;
  width: 100%;
  margin: 40px 0 0 0;
  ${(props: any): string => config(props).media.md`
    margin: 50px 0 0 0;
    max-width: 800px;
  `}
  ${(props: any): string => config(props).media.lg`
    max-width: 1100px;
  `}
`
const BottomImageContainerLink = styled.a`
  position: relative;
  width: 100%;
  margin: 40px 0 0 0;
  ${(props: any): string => config(props).media.md`
    margin: 50px 0 0 0;
    max-width: 800px;
  `}
  ${(props: any): string => config(props).media.lg`
    max-width: 1100px;
  `}
  transition: opacity .6s cubic-bezier(0.19,1,0.22,1);
  &:hover,
  &:focus {
    opacity: 0.8;
  }
  ${OutlineOnFocusVisible}
`
const TextContainer = styled.div`
  max-width: 900px;
`
const Header = styled.h1`
  color: ${(props: any): string =>
    props.isWhiteText ? colors.white : colors.sand};
  font-family: 'Queens Condensed Light';
  font-style: normal;
  font-weight: normal;
  line-height: 120%;
  margin: 0 0 20px 0;
  padding: 0;
  text-align: center;

  font-size: 36px;
  ${(props: any): string => config(props).media.md`
    font-size: 50px;
  `}
  ${(props: any): string => config(props).media.lg`
    font-size: 60px;
  `}
`
const Description = styled.div`
  color: ${(props: any): string =>
    props.isWhiteText ? colors.white : colors.sand};
  font-family: 'Good Sans Light';
  font-style: normal;
  font-weight: normal;
  line-height: 150%;
  padding: 0;
  margin: 0;
  text-align: center;

  font-size: 16px;
  ${(props: any): string => config(props).media.md`
    font-size: 18px;
  `}

  ol, li, ul, blockquote,img {
    display: none;
  }
  b {
    font-family: 'Good Sans Bold';
  }
`
const CtaContainer = styled.div`
  display: flex;
  justify-content: center;

  margin: 30px 0 0 0;
  ${(props: any): string => config(props).media.md`
    margin: 35px 0 0 0;
  `}
`
